import { useState, useCallback, createContext, useContext } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

/**
 * Brutalist Confirm Dialog — drop-in replacement for `window.confirm`.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Usunąć konto?",
 *     description: "Ta operacja jest nieodwracalna.",
 *     confirmText: "Usuń",
 *     cancelText: "Anuluj",
 *     variant: "danger", // or "default"
 *   });
 *   if (!ok) return;
 *
 * Requires <ConfirmProvider> mounted once high in the tree.
 */

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve } | null

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setState({ opts: opts || {}, resolve });
      }),
    []
  );

  const close = (result) => {
    if (state) state.resolve(result);
    setState(null);
  };

  const opts = state?.opts || {};
  const isDanger = (opts.variant || "danger") === "danger";
  const confirmBg = isDanger ? "bg-[#FCA5A5]" : "bg-[#FEF08A]";
  const iconColor = isDanger ? "text-[#B91C1C]" : "text-[#0F172A]";

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent
          className="brut border-4 border-[#0F172A] bg-white rounded-md p-0 gap-0 shadow-[8px_8px_0_#0F172A] max-w-md"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader className="p-5 border-b-2 border-[#0F172A] bg-[#F1F5F9]">
            <AlertDialogTitle className="font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${iconColor}`} strokeWidth={2.5} />
              {opts.title || "Potwierdzenie"}
            </AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="text-sm text-[#334155] font-medium pt-1">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="p-4 flex flex-row justify-end gap-2 space-x-0">
            <button
              onClick={() => close(false)}
              className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md px-4 py-2 font-bold text-sm uppercase"
              data-testid="confirm-dialog-cancel"
            >
              {opts.cancelText || "Anuluj"}
            </button>
            <button
              onClick={() => close(true)}
              className={`brut brut-hover ${confirmBg} border-2 border-[#0F172A] rounded-md px-4 py-2 font-black text-sm uppercase`}
              data-testid="confirm-dialog-confirm"
              autoFocus
            >
              {opts.confirmText || "Potwierdź"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback so component still works if provider is missing.
    return async (opts) =>
      window.confirm(`${opts?.title || ""}\n${opts?.description || ""}`.trim());
  }
  return ctx;
}
